import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import {
  Users,
  IndianRupee,
  FileText,
  AlertCircle,
  TrendingUp,
  Calendar,
  Clock,
  UserCheck,
  UserPlus,
  CheckCircle,
  ClipboardList,
  CreditCard,
  Clock3,
  ListChecks,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  PauseCircle,
  Gift,
  ExternalLink,
  Lock,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

import { formatCurrency, formatNumber } from "@/lib/utils";
import { toast } from "sonner";

// import { getSubscriptionStatus } from "@/services/subscriptionService";
import {
  getDashboardStats,
  getDashboardPendingActions,
  loanOverviewDashboard,
  getPayrollTrends,
  fetchDistribution,
  leaveStatistics,
  getActivityLogs,
  getTodayAttendanceSummary,
  getEmployees,
  fetchHolidays,
  fetchEvents,
  fetchCompanyLeaveConfiguration,
} from "@/services/adminService";
import { useAuth } from "@/contexts/AuthContext";
import AdminOnboardingWizard from "@/components/employee/OnBoarding";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { usePermission } from "@/contexts/RoleAccessContext";

// ----------------- MAIN DASHBOARD COMPONENT -----------------

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();


  const [stats, setStats] = useState({
    total_employees: 0,
    active_employees: 0,
    this_month_payroll: 0,
    payslips_generated: 0,
    upcoming_deadlines: 0,
    isOnboardingComplete: false,
    org_setup_steps: [],
    statutory_status: {},
    isCreatedBySuperAdmin: false
  });

  const [attendanceOverview, setAttendanceOverview] = useState({
    present: 0,
    absent: 0,
    late: 0,
    leave: 0,
    present_rate: 0,
    on_time_rate: 0,
    absentEmployees: [],
    lateEmployees: [],
    leaveEmployees: [],
  });

  const [pendingActions, setPendingActions] = useState({
    pending_leaves: 0,
    pending_loans: 0,
    pending_ot: 0,
    recent_late_arrivals: 0,
    total_pending: 0,
  });

  const [leaveStats, setLeaveStats] = useState({
    pending_leaves: 0,
    approved_this_month: 0,
    utilization_percentage: 0,
  });

  const [loanOverview, setLoanOverview] = useState({
    active_loans: 0,
    total_outstanding: 0,
    monthly_deductions: 0,
  });

  const [employeeDistribution, setEmployeeDistribution] = useState({
    by_department: [],
    by_designation: [],
  });

  const [payrollTrends, setPayrollTrends] = useState({ trends: [] });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const { subscriptionStatus, isPaidOnline, hasFeature } = useSubscription();
  // Departments are a paid feature — the API already returns an empty
  // by_department list without it, so hide the card rather than render blank.
  const canUseDepartments = hasFeature("department_mapping");
  const {
    isOnboardingComplete,
    orgSteps,
    statutoryStatus,
    reloadOnboardingStatus,
  } = useOnboarding();

  // Each card follows the access-role permission of the page it summarises, so
  // the dashboard never shows figures the admin can't open elsewhere. The API
  // enforces the same rules.
  const { can } = usePermission();
  const canSeeEmployees = can("employees", "view");
  const canAddEmployee = can("employees", "create");
  const canSeePayroll = can("run_payroll", "view") || can("payroll_analytics", "view");
  const canRunPayroll = can("run_payroll", "view");
  const canSeePayslips = can("payslips", "view");
  const canSeeLeaves = can("leave_management", "view");
  const canSeeLoans = can("loan_management", "view");
  const canSeeOvertime = can("overtime_management", "view");
  const canSeeAttendanceReports = can("attendance_reports", "view");
  const canSeeAttendanceDashboard = can("attendance_dashboard", "view");
  const canSeeActivityLogs = can("activity_logs", "view");
  const canSeeSubscription = can("subscription_management", "view");

  useEffect(() => {
    reloadOnboardingStatus();
    fetchAllDashboardData();
    fetchUpcomingEvents();
  }, []);

    const fetchUpcomingEvents = async () => {
      try {
        const events = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let allEmployees = [];

        // ===============================
        // 1️⃣ EMPLOYEES (Birthdays & Anniversaries)
        // ===============================
        try {
          if (canSeeEmployees) {
            const employeesResponse = await getEmployees();
            allEmployees = Array.isArray(employeesResponse)
              ? employeesResponse
              : employeesResponse?.data || [];

            const activeEmployees = allEmployees.filter(
              (emp) => emp.status === "active",
            );

            activeEmployees.forEach((emp) => {
              /* ---------------- BIRTHDAYS ---------------- */
              if (emp.date_of_birth) {
                const dob = new Date(emp.date_of_birth);

                let nextBirthday = new Date(
                  today.getFullYear(),
                  dob.getMonth(),
                  dob.getDate(),
                );
                nextBirthday.setHours(0, 0, 0, 0);

                if (nextBirthday < today) {
                  nextBirthday.setFullYear(today.getFullYear() + 1);
                }

                const daysUntil = Math.floor(
                  (nextBirthday - today) / (1000 * 60 * 60 * 24),
                );

                const age = nextBirthday.getFullYear() - dob.getFullYear();

                // Show only birthdays in current month
                if (daysUntil >= 0 && daysUntil <= 30) {
                  events.push({
                    type: "birthday",
                    title: `${emp.name}'s Birthday`,
                    subtitle: `Turns ${age}`,
                    date: nextBirthday,
                    daysUntil,
                    icon: "🎂",
                  });
                }
              }

              /* ---------------- ANNIVERSARIES ---------------- */
              if (emp.date_of_joining) {
                const joiningDate = new Date(emp.date_of_joining);

                let nextAnniversary = new Date(
                  today.getFullYear(),
                  joiningDate.getMonth(),
                  joiningDate.getDate(),
                );
                nextAnniversary.setHours(0, 0, 0, 0);

                if (nextAnniversary < today) {
                  nextAnniversary.setFullYear(today.getFullYear() + 1);
                }

                // Years completed *at* the upcoming anniversary date.
                const yearsWorked =
                  nextAnniversary.getFullYear() - joiningDate.getFullYear();

                const daysUntil = Math.floor(
                  (nextAnniversary - today) / (1000 * 60 * 60 * 24),
                );

                // yearsWorked < 1 means this date is the joining date itself
                // (today, or a future start), not a work anniversary. The raw
                // joining date is not an upcoming event, so skip it.
                if (yearsWorked >= 1 && daysUntil >= 0 && daysUntil <= 30) {
                  events.push({
                    type: "anniversary",
                    title: `${emp.name} - ${yearsWorked} Year${yearsWorked > 1 ? "s" : ""}`,
                    date: nextAnniversary,
                    daysUntil,
                    icon: "🎉",
                  });
                }
              }
            });
          }
        } catch (error) {
          console.error("Error fetching employees for events:", error);
        }

        // ===============================
        // 2️⃣ HOLIDAYS
        // ===============================
        try {
          const holidaysResponse = await fetchHolidays();
          const holidays = holidaysResponse || [];

          const upcomingHolidays = holidays
            .map((h) => ({ ...h, date: new Date(h.date) }))
            .filter((h) => {
              h.date.setHours(0, 0, 0, 0);
              const daysUntil = Math.floor(
                (h.date - today) / (1000 * 60 * 60 * 24),
              );
              return daysUntil >= 0 && daysUntil <= 30;
            })
            .map((h) => ({
              type: "holiday",
              title: h.name,
              date: h.date,
              daysUntil: Math.floor((h.date - today) / (1000 * 60 * 60 * 24)),
              icon: "🎊",
            }));

          events.push(...upcomingHolidays);
        } catch (error) {
          console.error("Error fetching holidays:", error);
        }

        // ===============================
        // 3️⃣ COMPANY EVENTS
        // ===============================
        try {
          const adminEventsResponse = await fetchEvents();
          const adminEvents = adminEventsResponse?.data?.events || [];

          const processed = adminEvents
            .map((event) => {
              const eventDate = new Date(event.date);
              eventDate.setHours(0, 0, 0, 0);

              const daysUntil = Math.floor(
                (eventDate - today) / (1000 * 60 * 60 * 24),
              );

              const icons = {
                company: "🏢",
                team: "👥",
                milestone: "🎯",
                other: "📅",
              };

              return {
                type: "event",
                title: event.title,
                subtitle: event.description || "",
                date: eventDate,
                daysUntil,
                icon: icons[event.event_type] || "📅",
              };
            })
            .filter((e) => e.daysUntil >= 0 && e.daysUntil <= 30);

          events.push(...processed);
        } catch (error) {
          console.error("Error fetching admin events:", error);
        }

        // ===============================
        // 4️⃣ LEAVES
        // ===============================
        try {
          const leavesResponse = await fetchCompanyLeaveConfiguration();
          const allLeaves = leavesResponse?.data?.leave_config || [];

          const nextWeek = new Date(today);
          nextWeek.setDate(today.getDate() + 7);

          const upcomingLeaves = allLeaves
            .filter((l) => l.status === "approved")
            .map((l) => ({
              ...l,
              start_date: new Date(l.start_date),
              end_date: new Date(l.end_date),
            }))
            .filter((l) => l.start_date <= nextWeek && l.end_date >= today);

          const leavesByEmployee = {};
          upcomingLeaves.forEach((l) => {
            if (!leavesByEmployee[l.employee_id]) {
              const emp = allEmployees.find(
                (e) => e.employee_id === l.employee_id,
              );
              leavesByEmployee[l.employee_id] = {
                employee_name: emp?.name || "Unknown",
                leaves: [],
              };
            }
            leavesByEmployee[l.employee_id].leaves.push(l);
          });

          Object.values(leavesByEmployee).forEach((empLeave) => {
            const earliest = empLeave.leaves.sort(
              (a, b) => a.start_date - b.start_date,
            )[0];

            const daysUntil = Math.floor(
              (earliest.start_date - today) / (1000 * 60 * 60 * 24),
            );

            const isToday =
              earliest.start_date <= today && earliest.end_date >= today;

            events.push({
              type: "leave",
              title: empLeave.employee_name,
              subtitle: isToday
                ? "On leave today"
                : `Leave starts ${format(earliest.start_date, "MMM dd")}`,
              date: earliest.start_date,
              daysUntil,
              icon: "🏖️",
            });
          });
        } catch (error) {
          console.error("Error fetching leaves:", error);
        }

        // ===============================
        // FINAL SORT
        // ===============================
        events.sort((a, b) => {
          if (a.daysUntil === b.daysUntil) {
            if (a.type === "leave" && b.type !== "leave") return -1;
            if (b.type === "leave" && a.type !== "leave") return 1;
            return 0;
          }
          return a.daysUntil - b.daysUntil;
        });

        setUpcomingEvents(events);
      } catch (error) {
        console.error("Error fetching upcoming events:", error);
      }
    };
 
  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);
      setActivitiesLoading(true);

      await Promise.all([
        fetchDashboardStats(),
        fetchPendingActions(),
        canSeeLeaves && fetchLeaveStats(),
        canSeeLoans && fetchLoanOverview(),
        canSeeEmployees && fetchEmployeeDistribution(),
        canSeePayroll && fetchPayrollTrends(),
        canSeeActivityLogs && fetchRecentActivities(),
        // fetchSubscriptionStatus(),
        canSeeAttendanceDashboard && fetchAttendanceOverview(),
      ]);
    } catch (error) {
      console.error("Error  loading dashboard:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setActivitiesLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await getDashboardStats(user.companyId);
      setStats((prev) => ({
        ...prev,
        ...response,
      }));
      
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      toast.error("Failed to load dashboard statistics");
    }
  };

  const fetchAttendanceOverview = async () => {
    try {
      const response = await getTodayAttendanceSummary();
      setAttendanceOverview(response);
    } catch (error) {
      console.error("Error fetching attendance overview:", error);
    }
  };

  const fetchPendingActions = async () => {
    try {
      const response = await getDashboardPendingActions();
      setPendingActions(response.data);
    } catch (error) {
      console.error("Error fetching pending actions:", error);
    }
  };

  const fetchLeaveStats = async () => {
    try {
      const response = await leaveStatistics();
      setLeaveStats(response.data);
    } catch (error) {
      console.error("Error fetching leave statistics:", error);
    }
  };

  const fetchLoanOverview = async () => {
    try {
      const response = await loanOverviewDashboard();
      setLoanOverview(response.data);
    } catch (error) {
      console.error("Error fetching loan overview:", error);
    }
  };

  const fetchEmployeeDistribution = async () => {
    try {
      const response = await fetchDistribution();
      setEmployeeDistribution(response.data.distribution);
    } catch (error) {
      console.error("Error fetching employee distribution:", error);
    }
  };

  const fetchPayrollTrends = async () => {
    try {
      const response = await getPayrollTrends();
      setPayrollTrends(response.data);
    } catch (error) {
      console.error("Error fetching payroll trends:", error);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      setActivitiesLoading(true);
      try {
        const res = await getActivityLogs({
          page: 1,
          limit: 20,
        });

        // API returns { logs, pagination }
        setRecentActivities(res || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching recent activities:", error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatActivityTime = (timestamp) => {
    if (!timestamp) return "Recently";

    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor(
      (now.getTime() - activityTime.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const getActivityType = (category, notificationType) => {
    if (category === "birthday") return "info";
    if (category === "leave") return "warning";
    if (category === "payslip" || category === "compliance") return "success";
    if (category === "employee") return "info";
    if (notificationType === "success") return "success";
    if (notificationType === "error" || category === "error") return "error";
    return "info";
  };

  const statCards = useMemo(
    () => [
      {
        locked: !canSeeEmployees,
        lockLabel: "employee data",
        title: "Active Employees",
        value: canSeeEmployees ? formatNumber(stats.active_employees || 0) : "—",
        icon: Users,
        color: "text-blue-500",
        description: "Total workforce currently active",
        change: "+12%",
        changeType: "positive",
      },
      {
        locked: !canSeePayroll,
        lockLabel: "payroll",
        title: "Monthly Payroll Value",
        value: canSeePayroll ? formatCurrency(stats.this_month_payroll || 0) : "—",
        icon: IndianRupee,
        color: "text-green-500",
        description: "Payout from the latest payroll run",
        change: "+8%",
        changeType: "positive",
      },
      {
        locked: !canSeePayslips,
        lockLabel: "payslips",
        title: "Payslips Generated",
        value: canSeePayslips ? formatNumber(stats.payslips_generated || 0) : "—",
        icon: FileText,
        color: "text-yellow-500",
        description: "Payslips finalized for the last cycle",
        change: "100%",
        changeType: "neutral",
      },
      // {
      //   title: "Upcoming Deadlines",
      //   value: formatNumber(stats.upcoming_deadlines || 0),
      //   icon: AlertCircle,
      //   color: "text-red-500",
      //   description: "Critical compliance items due soon",
      //   change: "3 Due",
      //   changeType: "negative",
      // },
    ],
    [stats, canSeeEmployees, canSeePayroll, canSeePayslips],
  );

  const pendingActionItems = [
    {
      locked: !canSeeLeaves,
      label: "Leave Approvals",
      count: pendingActions.pending_leaves,
      icon: Calendar,
      color:
        "bg-orange-50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
      path: "/leave-management",
    },
    {
      locked: !canSeeLoans,
      label: "Loan Requests",
      count: pendingActions.pending_loans,
      icon: IndianRupee,
      color:
        "bg-purple-50 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
      path: "/loan-management",
    },
    {
      locked: !canSeeOvertime,
      label: "Overtime Approvals",
      count: pendingActions.pending_ot,
      icon: Clock,
      color:
        "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
      path: "/attendance/shifts",
      state: { tab: "overtime" },
    },
    {
      locked: !canSeeAttendanceReports,
      label: "Late Arrivals (Last 7 Days)",
      count: pendingActions.recent_late_arrivals,
      icon: AlertCircle,
      color: "bg-red-50 text-red-700 dark:bg-red-900/40 dark:text-red-300",
      path: "/attendance/reports",
      state: { tab: "late" },
    },
  ];

  const quickActions = [
    {
      locked: !canAddEmployee,
      label: "Add New Employee",
      icon: UserPlus,
      path: "/employees/new",
      primary: true,
    },
    { locked: !canRunPayroll, label: "Run Payroll", icon: IndianRupee, path: "/payroll/run" },
    { locked: !canSeeLeaves, label: "Review Leaves", icon: CheckCircle, path: "/leave-management" },
    {
      locked: !canSeeAttendanceDashboard,
      label: "Manage Attendance",
      icon: ClipboardList,
      path: "/attendance/dashboard",
    },
  ];

  if (loading) {
    return (
      <div className="space-y-8 p-6 min-h-screen">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-9 w-72" />
            <Skeleton className="h-4 w-96" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6 space-y-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-36" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-12">
          <Card className="lg:col-span-8">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-6 w-44" />
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </CardContent>
          </Card>
          <Card className="lg:col-span-4">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-6 w-40" />
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // If onboarding incomplete → show wizard
  if (!isOnboardingComplete) {
    return (
      <div className="p-8 min-h-screen">
        <AdminOnboardingWizard
          steps={orgSteps}
          statutory_status={statutoryStatus}
        />
      </div>
    );
  }

  // MAIN DASHBOARD UI
  return (
    <div className="space-y-8 min-h-screen">
      {/* ---------------- HEADER ---------------- */}
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white">
            Payroll & HR Overview
          </h1>

          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Real-time performance metrics and actionable insights for your
            organization.
          </p>
        </div>

        <div className="flex space-x-3 mt-4 sm:mt-0">
          {/* <Button variant="outline" size="sm" className="group">
            <Calendar className="w-4 h-4 mr-2 text-gray-500 group-hover:text-teal-600" />
            <span className="dark:text-white">This Month</span>
          </Button> */}

          {/* <Button
            size="sm"
            variant="default"
            onClick={() => navigate("/reports/generate")}
          >
            Generate Report
          </Button> */}
        </div>
      </header>

      {/* ---------------- SUBSCRIPTION BANNER ---------------- */}
      {canSeeSubscription && subscriptionStatus ? (
        <SubscriptionBanner status={subscriptionStatus} isPaidOnline={isPaidOnline} isCreatedBySuperAdmin={stats.isCreatedBySuperAdmin} navigate={navigate} />
      ) : null}

      {/* ---------------- STATS CARDS (TOP GRID) ---------------- */}
      {/* Every card keeps its place; ones the admin's role doesn't cover are
          blurred behind a lock (LockedCard) instead of disappearing. */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((card, index) => (
          <LockedCard key={index} locked={card.locked} label={card.lockLabel}>
            <StatCard {...card} />
          </LockedCard>
        ))}
      </div>
      <div className="grid gap-6">
        {/* ---------------- ROW 1 : 3 CARDS ---------------- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <UpcomingEventsCard events={upcomingEvents} />

          {/* EMPLOYEE DISTRIBUTION (departments are a plan feature) */}
          {canUseDepartments && (
            <LockedCard locked={!canSeeEmployees} label="employee data">
              <EmployeeDistributionCard data={employeeDistribution.by_department} />
            </LockedCard>
          )}

          {/* ACTION REQUIRED */}
          <LockedCard
            locked={pendingActionItems.every((item) => item.locked)}
            label="these approvals"
          >
            <PendingActionsCard actions={pendingActionItems} navigate={navigate} />
          </LockedCard>
        </div>

        {/* ---------------- ROW 2 ---------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT (Attendance + Leave + Loan) */}
          <div className="lg:col-span-2 grid md:grid-cols-2 gap-6">
            <LockedCard locked={!canSeeAttendanceDashboard} label="attendance">
              <AttendanceCard attendance={attendanceOverview} />
            </LockedCard>

            <div className="space-y-6">
              <LockedCard locked={!canSeeLeaves} label="leave">
                <LeaveCard leaveStats={leaveStats} />
              </LockedCard>
              <LockedCard locked={!canSeeLoans} label="loans">
                <LoanCard loanOverview={loanOverview} />
              </LockedCard>
            </div>
          </div>

          {/* RIGHT (Quick Actions) */}
          <LockedCard
            locked={quickActions.every((action) => action.locked)}
            label="these actions"
          >
            <QuickActionsCard actions={quickActions} navigate={navigate} />
          </LockedCard>
        </div>

        {/* ---------------- ROW 3 ---------------- */}
        <LockedCard locked={!canSeePayroll} label="payroll">
          <PayrollTrendsCard trends={payrollTrends.trends} />
        </LockedCard>
      </div>

      {/* ---------------- RECENT ACTIVITY LOG ---------------- */}
      <LockedCard locked={!canSeeActivityLogs} label="activity logs">
        <RecentActivitiesCard
          activities={recentActivities}
          loading={activitiesLoading}
          formatTime={formatActivityTime}
          getType={getActivityType}
        />
      </LockedCard>
    </div>
  );
};
export default Dashboard;

// A card the admin's role doesn't cover: kept in place so the layout never
// shifts, but blurred behind a lock. It only ever holds placeholder content —
// nothing is fetched for it, and the API refuses it anyway.
const LockedCard = ({ locked, label, children }) => {
  if (!locked) return children;
  return (
    // No h-full here: as a grid cell the wrapper already stretches to the row,
    // and in a stacked column (leave + loans) h-full made it as tall as the
    // whole column, pushing the centred message below the card.
    <div className="relative" title={`You don't have access to ${label}`}>
      <div
        aria-hidden="true"
        inert=""
        className="h-full pointer-events-none select-none blur-[3px] opacity-50"
      >
        {children}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-background/80 shadow-sm">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </span>
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-foreground">
            You don't have access to {label}
          </p>
          <p className="text-xs text-muted-foreground">
            Contact your administrator to get access.
          </p>
        </div>
      </div>
    </div>
  );
};

const UpcomingEventsCard = ({ events }) => (
  <Card className="h-full rounded-2xl border border-purple-200/40 bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl shadow-[0_12px_40px_rgba(15,23,42,0.1)] dark:border-purple-900/40">
    <CardHeader className="pb-2">
      <CardTitle className="flex items-center  text-purple-700 dark:text-purple-400">
        <Gift className="h-5 w-5" />
        Upcoming Events
      </CardTitle>
    </CardHeader>
    <CardContent className="max-h-[320px] overflow-y-auto pr-2 space-y-3">
      {events.length === 0 && (
        <div className="py-10 text-center text-slate-500 dark:text-slate-400">
          <Calendar className="h-10 w-10 mx-auto mb-2 text-slate-400" />
          No upcoming events
        </div>
      )}
      {events.map((event, index) => (
        <div
          key={index}
          className="rounded-xl border border-purple-300/40 bg-gradient-to-r from-purple-50/80 to-blue-50/60 dark:from-purple-900/20 dark:to-blue-900/20 p-3 flex justify-between items-center hover:shadow-md transition"
        >
          <div className="flex items-start gap-3 min-w-0">
            <div className="text-2xl">{event.icon}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                {event.title}
                {event.subtitle && (
                  <span className="ml-1 text-purple-600 dark:text-purple-300">
                    {event.subtitle}
                  </span>
                )}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
               {new Date(event.date).toDateString()}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <p
              className={`text-lg font-bold ${
                event.daysUntil === 0
                  ? "text-red-600 dark:text-red-400"
                  : event.daysUntil <= 7
                  ? "text-orange-600 dark:text-orange-400"
                  : "text-purple-700 dark:text-purple-300"
              }`}
            >
              {event.daysUntil === 0 ? "Today" : event.daysUntil}
            </p>
            {event.daysUntil > 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {event.daysUntil === 1 ? "day" : "days"}
              </p>
            )}
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const PendingActionsCard = ({ actions: actionsList, navigate }) => {
  // Counts only queues the admin can see (locked rows carry no data).
  const totalPending = actionsList
    .filter((item) => !item.locked)
    .reduce((sum, item) => sum + (Number(item.count) || 0), 0);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2 text-red-600 dark:text-red-400">
          <AlertCircle className="h-5 w-5" />
          <span>Action Required ({totalPending})</span>
        </CardTitle>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Items needing your immediate attention.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {actionsList.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              type="button"
              disabled={item.locked}
              title={item.locked ? "You don't have access to this" : undefined}
              className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 ${
                item.locked
                  ? "cursor-not-allowed bg-muted/50 text-muted-foreground"
                  : `cursor-pointer hover:shadow-lg ${item.color}`
              }`}
              onClick={() => navigate(item.path, { state: item.state })}
            >
              <div className="flex items-center space-x-3">
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
              {item.locked ? (
                <Lock className="h-4 w-4" />
              ) : (
                <Badge
                  variant="secondary"
                  className="min-w-[30px] justify-center"
                >
                  {item.count ?? 0}
                </Badge>
              )}
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const StatCard = ({
  title,
  value,
  icon: Icon,
  color,
  description,
  change,
  changeType,
}) => {
  const isPositive = changeType === "positive";
  const isNegative = changeType === "negative";

  const changeColor = isPositive
    ? "text-green-600 dark:text-green-400"
    : isNegative
    ? "text-red-600 dark:text-red-400"
    : "text-gray-500 dark:text-gray-400";

  const ChangeIcon = isPositive ? ChevronUp : isNegative ? ChevronDown : null;

  return (
    <Card className="hover:ring-2 hover:ring-teal-500/50 transition-all duration-300">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
          {title}
        </p>
        <Icon className={`h-5 w-5 ${color}`} />
      </CardHeader>

      <CardContent>
        <div className="flex items-end justify-between">
          <div className="text-3xl font-extrabold text-gray-900 dark:text-white">
            {value}
          </div>

          {/* <div
            className={`flex items-center text-sm font-semibold ${changeColor}`}
          >
            {ChangeIcon && <ChangeIcon className="h-4 w-4 mr-0.5" />}
            {change}
          </div> */}
        </div>

        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
          {description}
        </p>
      </CardContent>
    </Card>
  );
};

const calculateTrialDaysLeft = (trialEndTimestamp,isCreatedBySuperAdmin ) => {
  if (!trialEndTimestamp) return 0;

  const trialEnd = new Date(trialEndTimestamp);
  const now = new Date(); // Current client time

  // Calculate difference in milliseconds
  const diffMs = trialEnd - now;

  // Convert to days (floor to whole days)
  const daysLeft = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, daysLeft); // Prevent negative values
};

const SubscriptionBanner = ({ status, isPaidOnline, isCreatedBySuperAdmin, navigate }) => {
  const trialDaysLeft =
    status.status === "trial" || status.status === "trial_expired"
      ? calculateTrialDaysLeft(status.trial_end_date,isCreatedBySuperAdmin ) // assuming backend sends timestamp
      : 0;

  if (status.status === "trial") {
    return (
      <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-xl shadow-md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <Clock3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h3 className="font-bold text-blue-900 dark:text-blue-100">
                Trial Period Active
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                You have{" "}
                <b>
                  {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}
                </b>{" "}
                left in your trial.
              </p>
            </div>
          </div>

          <Button
            disabled={!isPaidOnline}
            onClick={() => navigate("/subscription")}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  if (status.status === "trial_expired") {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-xl shadow-md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="font-bold text-red-900 dark:text-red-100">
                Trial Period Ended
              </h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                Please subscribe immediately to restore access.
              </p>
            </div>
          </div>

          <Button
            disabled={!isPaidOnline}
            onClick={() => navigate("/subscription")}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Subscribe Now
          </Button>
        </div>
      </div>
    );
  }

  if (status.status === "active") {
    const daysUntilExpiry = status.next_billing_date
      ? Math.ceil((new Date(status.next_billing_date) - new Date()) / (1000 * 60 * 60 * 24))
      : null;

    const isExpiryNear = daysUntilExpiry && daysUntilExpiry <= 7 && daysUntilExpiry > 0;

    const cardClass = isExpiryNear
      ? 'border-orange-400/50 dark:border-orange-700/50 bg-orange-50/50 dark:bg-orange-900/30'
      : 'border-green-400/50 dark:border-green-700/50 bg-green-50/50 dark:bg-green-900/30';

    const titleClass = isExpiryNear
      ? 'text-orange-900 dark:text-orange-100'
      : 'text-green-800 dark:text-green-200';

    const textClass = isExpiryNear
      ? 'text-orange-700 dark:text-orange-300'
      : 'text-green-700 dark:text-green-300';

    const IconComponent = isExpiryNear ? AlertTriangle : CheckCircle;
    const iconColorClass = isExpiryNear
      ? 'text-orange-600 dark:text-orange-400'
      : 'text-green-600 dark:text-green-400';

    const ButtonIcon = isExpiryNear ? CreditCard : TrendingUp;
    const buttonText = isExpiryNear ? 'Renew Now' : 'Manage Subscription';

    return (
      <Card className={cardClass}>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
            <div className="flex items-start gap-3">
              <IconComponent className={`h-5 w-5 ${iconColorClass}`} />
              <div>
                <h3 className={`font-medium ${titleClass}`}>
                  {isExpiryNear ? (
                    <>Plan Expiry Coming Soon – <b>{status.plan}</b></>
                  ) : (
                    <>Subscription Active – <b>{status.plan}</b></>
                  )}
                </h3>
                <p className={`text-sm ${textClass}`}>
                  {isExpiryNear ? (
                    <>Expires in <b>{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</b> on <b>{new Date(status.next_billing_date).toLocaleDateString('en-IN')}</b></>
                  ) : (
                    <>
                      Next billing date:{" "}
                      <b>
                        {status.next_billing_date
                          ? new Date(status.next_billing_date).toLocaleDateString()
                          : "N/A"}
                      </b>
                    </>
                  )}
                </p>
              </div>
            </div>

            <Button
              disabled={!isPaidOnline}
              onClick={() => navigate("/subscription")}
              variant={isExpiryNear ? "default" : "outline"}
              size="sm"
              className={isExpiryNear ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'border-green-400 dark:border-green-600'}
            >
              <ButtonIcon className="mr-2 h-4 w-4" />
              {buttonText}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status.status === "past_due") {
    return (
      <div className="p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-xl shadow-md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-700 dark:text-amber-300" />
            <div>
              <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                Payment could not be processed
              </h3>

              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                Your subscription is still active, but renewal failed. Please
                update your payment method to avoid interruption.
              </p>
            </div>
          </div>

          <Button
            disabled={!isPaidOnline}
            onClick={() => navigate("/subscription")}
            className="bg-amber-700 hover:bg-amber-800 text-white"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            Fix payment
          </Button>
        </div>
      </div>
    );
  }

  if (status.status === "paused") {
    return (
      <div className="p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div>
              <h3 className="font-bold text-blue-900 dark:text-blue-100">
                Your subscription is paused
              </h3>

              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Billing has been paused at your request. You still have full
                access — but future renewals will not occur until you resume.
              </p>

              {status.next_billing_date && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                  Resume anytime to continue uninterrupted beyond{" "}
                  <b>
                    {new Date(status.next_billing_date).toLocaleDateString()}
                  </b>
                  .
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const AttendanceCard = ({ attendance }) => {
  const [openEmployeeListDialog, setOpenEmployeeListDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { present, absent, late, leave, present_rate, on_time_rate } =
    attendance;
  const total = (present || 0) + (absent || 0) + (late || 0) + (leave || 0);

  const items = [
    { label: "Present", value: present, color: "bg-teal-500", icon: UserCheck },
    { label: "Absent", value: absent, color: "bg-red-500", icon: AlertCircle },
    { label: "Late", value: late, color: "bg-yellow-500", icon: Clock },
    { label: "On Leave", value: leave, color: "bg-blue-500", icon: Calendar },
  ];

  const employeeMap = {
    Present: attendance.presentEmployees || [],
    Absent: attendance.absentEmployees || [],
    Late: attendance.lateEmployees || [],
    "On Leave": attendance.leaveEmployees || [],
  };

  const showRelatedEmployee = (item) => {
    setSelectedCategory(item.label);
    setOpenEmployeeListDialog(true);
  };

  return (
    <>
      {/* ===================== CARD ===================== */}
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
            <UserCheck className="h-5 w-5" />
            Today's Attendance Snapshot
          </CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Total Tracked: {total}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Attendance Categories */}
          <div className="grid grid-cols-2 gap-4">
            {items.map((it, i) => {
              const Icon = it.icon;
              return (
                <div
                  key={i}
                  className="relative flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer"
                  onClick={() => showRelatedEmployee(it)}
                >
                  {/* External link (top-right) */}
                  <div
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      showRelatedEmployee(it);
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </div>

                  {/* Icon */}
                  <div
                    className={`h-8 w-8 flex items-center justify-center text-white rounded-full ${it.color}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-2xl font-bold dark:text-white">
                      {it.value}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {it.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress Bars */}
          <ProgressDisplay
            label="Present Rate"
            value={present_rate}
            color="bg-teal-500"
          />
          <ProgressDisplay
            label="On-Time Arrival"
            value={on_time_rate}
            color="bg-blue-500"
          />
        </CardContent>
      </Card>

      {/* ===================== DIALOG ===================== */}
      <Dialog
        open={openEmployeeListDialog}
        onOpenChange={setOpenEmployeeListDialog}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedCategory} Employees</DialogTitle>
            <DialogDescription>
              List of employees marked as {selectedCategory?.toLowerCase()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {(employeeMap[selectedCategory] || []).map((emp, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                <div className="w-full flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {emp.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {emp.designation}
                    </p>
                  </div>
                  <p className="text-xs text-gray-400">ID: {emp.employeeId}</p>
                </div>
              </div>
            ))}

            {(employeeMap[selectedCategory] || []).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No employees found
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ProgressDisplay = ({ label, value, color }) => (
  <div>
    <div className="flex justify-between text-sm mb-1 font-medium dark:text-gray-200">
      <span>{label}</span>
      <span className="font-extrabold text-gray-700 dark:text-gray-100">
        {Math.round(value || 0)}%
      </span>
    </div>

    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
      <div
        className={`${color} h-2.5 rounded-full`}
        style={{ width: `${Math.min(Math.max(value || 0, 0), 100)}%` }}
      ></div>
    </div>
  </div>
);

const LeaveCard = ({ leaveStats }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
        <Calendar className="h-5 w-5" />
        Leave Management Metrics
      </CardTitle>
    </CardHeader>

    <CardContent className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-center">
        <MetricBox
          label="Pending"
          value={leaveStats.pending_leaves}
          color="text-orange-600"
        />
        <MetricBox
          label="Approved This Month"
          value={leaveStats.approved_this_month}
          color="text-green-600"
        />
      </div>

      <ProgressDisplay
        label="Annual Leave Utilization"
        value={leaveStats.utilization_percentage}
        color="bg-purple-500"
      />
    </CardContent>
  </Card>
);

const LoanCard = ({ loanOverview }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
        <CreditCard className="h-5 w-5" />
        Employee Loan Portfolio
      </CardTitle>
    </CardHeader>

    <CardContent className="space-y-3">
      <MetricBox
        label="Active Loans"
        value={loanOverview.active_loans}
        color="text-indigo-600"
      />
      <MetricBox
        label="Total Outstanding"
        value={formatCurrency(loanOverview.total_outstanding)}
        color="text-red-600"
      />
      <MetricBox
        label="Monthly Deductions"
        value={formatCurrency(loanOverview.monthly_deductions)}
        color="text-green-600"
      />
    </CardContent>
  </Card>
);

const MetricBox = ({ value, label, color }) => (
  <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
    <div className={`font-extrabold text-2xl ${color}`}>{value}</div>
    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{label}</div>
  </div>
);

const PayrollTrendsCard = ({ trends }) => {
  if (!trends || trends.length === 0) {
    return (
      <Card className="min-h-[480px]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <TrendingUp className="h-5 w-5" />
            Payroll Spend Trend
          </CardTitle>
        </CardHeader>

        <CardContent className="text-center py-10 text-gray-500 dark:text-gray-400">
          No payroll history available.
        </CardContent>
      </Card>
    );
  }

  const max = Math.max(...trends.map((t) => t.amount || 0));

  return (
    <Card className="min-h-[480px]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-600 dark:text-green-400">
          <TrendingUp className="h-5 w-5" />
          Payroll Spend Trend
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {trends.map((t, i) => (
          <div key={i}>
            <div className="flex justify-between text-sm font-medium">
              <span>{t.month}</span>
              <span>{formatCurrency(t.amount)}</span>
            </div>

            <ProgressDisplay
              value={max ? (t.amount / max) * 100 : 0}
              color="bg-green-500"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const QuickActionsCard = ({ actions, navigate }) => {

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
          <ListChecks className="h-5 w-5" />
          Quick Actions
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-3">
        {actions.map((a, i) => {
          const Icon = a.icon;
          return (
            <Button
              key={i}
              onClick={() => navigate(a.path)}
              disabled={a.locked}
              title={a.locked ? "You don't have access to this" : undefined}
              variant={a.primary && !a.locked ? "default" : "outline"}
              className={`w-full h-12 flex items-center gap-3 ${
                a.locked
                  ? "text-muted-foreground"
                  : a.primary
                    ? "bg-teal-600 hover:bg-teal-700 text-white"
                    : "border-teal-500"
              }`}
            >
              <Icon className="h-5 w-5" />
              {a.label}
              {a.locked && <Lock className="ml-auto h-4 w-4" />}
            </Button>
          );
        })}
      </CardContent>
    </Card>
  );
};

const EmployeeDistributionCard = ({ data }) => {
  const COLORS = [
    "#3b82f6",
    "#10b981",
    "#f59e0b",
    "#ef4444",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
  ];

  // One height for both the chart and its empty state so the card doesn't resize
  // when data arrives, and stays in step with the other chart cards.
  const CHART_HEIGHT = 288;

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-teal-600 dark:text-teal-400">
          <Users className="h-5 w-5" />
          Employee Distribution
        </CardTitle>
      </CardHeader>

      <CardContent>
        {data && data.length > 0 ? (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <PieChart>
              <Pie
                data={data.map((d) => ({
                  name: String(d.name),
                  value: Number(d.count),
                }))}
                cx="50%"
                cy="50%"
                outerRadius="70%"
                dataKey="value"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>

              {/* Themed to match the other dashboard charts — the default
                  tooltip is an unstyled white box in dark mode. */}
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--background))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "hsl(var(--foreground))" }}
                itemStyle={{ color: "hsl(var(--foreground))" }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div
            className="flex items-center justify-center text-gray-500 dark:text-gray-400"
            style={{ height: CHART_HEIGHT }}
          >
            No data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const RecentActivitiesCard = ({ activities, loading, formatTime, getType }) => {
  const variant = (type) => {
    if (type === "error") return "destructive";
    if (type === "success") return "default";
    if (type === "warning") return "secondary";
    return "secondary";
  };

  const formatAction = (action) => {
    if (!action) return "";
    return action
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
          <Clock className="h-5 w-5 text-gray-500" />
          System Activity Log
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="space-y-6 max-h-[400px] overflow-y-auto pr-2">
          {loading ? (
            <div className="space-y-3 py-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : activities.length > 0 ? (
            <div className="relative ml-4">
              {/* Vertical Timeline Line */}
              <div className="absolute left-2 top-0 h-full w-px bg-gray-300 dark:bg-gray-700" />

              {activities.map((a, i) => {
                const type = getType(a.category);

                const colors = {
                  success: "bg-teal-500",
                  warning: "bg-yellow-500",
                  error: "bg-red-500",
                  info: "bg-primary",
                  default: "bg-gray-400",
                };

                return (
                  <div key={i} className="relative pl-8 pb-6">
                    {/* Timeline Dot */}
                    <span
                      className={`absolute left-2 top-1.5 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-background dark:border-gray-900 ${
                        colors[type] || colors.default
                      }`}
                    />

                    {/* Title + Time */}
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-semibold text-gray-200">
                        {formatAction(a.action)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatTime(a.timestamp)}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-400 mt-1 leading-snug">
                      {a.description}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">No recent activity</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
